import React, { useState, useEffect } from 'react';
import { FaDownload, FaSearch, FaCalendarAlt, FaInfoCircle, FaEye } from 'react-icons/fa';
import { FiAlertTriangle, FiSunrise, FiSunset, FiArrowDownCircle, FiCoffee } from 'react-icons/fi';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Swal from 'sweetalert2';
import useAxios from '../utils/useAxios';
import { useSidebar } from '../context/SidebarContext';
import OrganisationSelector from '../components/OrganisationSelector';
import styles from '../styles/JustificationPage';
import { useAuth } from '../context/AuthContext';

const JustificationPage = () => {
  const [presences, setPresences] = useState([]);
  const [pagination, setPagination] = useState({ 
    count: 0, 
    next: null, 
    previous: null, 
    totalPages: 1 
  });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedStatut, setSelectedStatut] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedPresence, setSelectedPresence] = useState(null);
  const [justifTexte, setJustifTexte] = useState('');
  const [justifFile, setJustifFile] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const axiosInstance = useAxios();
  const { sidebarVisible } = useSidebar();
  const { user } = useAuth();
  // États pour la gestion des organisations
  const [selectedOrganisation, setSelectedOrganisation] = useState('');
  const [currentOrganisation, setCurrentOrganisation] = useState(null);

  const formatHeure = (heure) => (heure ? heure.split('.')[0] : '-');

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailJustif, setDetailJustif] = useState(null);

  const getCurrentPageFromUrls = () => {
    if (pagination.previous) {
      const pageMatch = pagination.previous.match(/page=(\d+)/);
      if (pageMatch) {
        return parseInt(pageMatch[1]) + 1;
      }
    } else if (pagination.next) {
      const pageMatch = pagination.next.match(/page=(\d+)/);
      if (pageMatch) {
        return parseInt(pageMatch[1]) - 1;
      }
    }
    return 1;
  };

  const currentPage = getCurrentPageFromUrls();

  const handleOrganisationChange = (orgId, org) => {
    setSelectedOrganisation(orgId);
    setCurrentOrganisation(org);
    fetchPresences(1, orgId);
  };

  const fetchPresences = async (targetPage = 1, orgId = selectedOrganisation, useUrl = null) => {
    if (!orgId) return; 
    setLoading(true);
    try {
      let url;
      if (useUrl) {
        url = useUrl;
      } else {
        const params = new URLSearchParams({
          page: targetPage,
          search,
          date: selectedDate,
          statut: selectedStatut,
          organisation: orgId,
          page_size: pageSize === 'all' ? totalCount : pageSize
        });
        url = `/pointage/presences/non-justifiees/?${params}`;
      }
      
      const response = await axiosInstance.get(url);
      setPresences(response.data.results);
      setTotalCount(response.data.count);

      if (pageSize === 'all') {
        setPagination({ 
          count: response.data.count,
          next: response.data.next,
          previous: response.data.previous,
          totalPages: 1 
        });
      } else {
        setPagination({ 
          count: response.data.count,
          next: response.data.next,
          previous: response.data.previous,
          totalPages: Math.ceil(response.data.count / pageSize) 
        });
      }

      if (!useUrl) {
        setPage(targetPage);
      }
    } catch (err) {
      console.error("Erreur lors du chargement des présences", err);
      Swal.fire({ 
        icon: 'error', 
        title: 'Erreur', 
        text: 'Impossible de charger les présences.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const generatePageSizeOptions = () => {
    const options = [10, 20, 30, 50];
    
    if (totalCount > 50 && !options.includes(totalCount)) {
      options.push(totalCount);
    }
    
    options.push('all');
    return options;
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) fetchPresences(newPage);
  };

  const handleNextPage = () => {
    if (pagination.next) {
      fetchPresences(null, selectedOrganisation, pagination.next);
    }
  };

  const handlePrevPage = () => {
    if (pagination.previous) {
      fetchPresences(null, selectedOrganisation, pagination.previous);
    }
  };

  useEffect(() => {
    if (selectedOrganisation) {
      fetchPresences(1);
    }
  }, [search, selectedDate, selectedStatut, pageSize, selectedOrganisation]);

  const handleJustifier = async () => {
    if (!selectedPresence || !justifTexte) {
      Swal.fire({ icon: 'warning', title: 'Texte requis', confirmButtonColor: '#1B019B' });
      return;
    }
    const formData = new FormData();
    formData.append('presence', selectedPresence.id);
    formData.append('texte', justifTexte);
    if (justifFile) formData.append('fichier', justifFile);

    try {
      await axiosInstance.post('/pointage/justifications/creer/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      Swal.fire({
        icon: 'success',
        title: 'Justification enregistrée',
        confirmButtonColor: '#1B019B'
      });
      setShowModal(false);
      setJustifTexte('');
      setJustifFile(null);
     
      fetchPresences(currentPage);
    } catch (error) {
      console.error(error);
      Swal.fire({ icon: 'error', title: 'Erreur lors de l\'enregistrement', confirmButtonColor: '#1B019B' });
    }
  };

  const fetchAllPresences = async () => {
    try {
      let allPresences = [];
      let nextUrl = `/pointage/presences/non-justifiees/?search=${search}&date=${selectedDate}&statut=${selectedStatut}&organisation=${selectedOrganisation}&page_size=1000`;
      
      while (nextUrl) {
        const response = await axiosInstance.get(nextUrl);
        allPresences = [...allPresences, ...response.data.results];
        nextUrl = response.data.next;
      }
      return allPresences;
    } catch (err) {
      console.error("Erreur lors de la récupération de toutes les présences", err);
      return [];
    }
  };

  const handleExportPDF = async () => {
    setLoading(true);
    try {
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text(`Rapport des présences à justifier - ${currentOrganisation?.nom || ''}`, 14, 20);

      const allPresences = await fetchAllPresences();

  
      const tableColumn = ["Nom", "Prénom", "Date", "Heure", "Période", "Statut", "Justifié"];
      const tableRows = [];

      allPresences.forEach((p) => {
        
        let periodeFormatee = '-';
        if (p.periode === "autre") {
          periodeFormatee = "Exceptionnelle";
        } else if (p.periode) {
          periodeFormatee = p.periode.charAt(0).toUpperCase() + p.periode.slice(1).replace('_', ' ');
        }

        tableRows.push([
          p.employe_nom,
          p.employe_prenom,
          p.date || '-',
          formatHeure(p.heure), 
          periodeFormatee, 
          p.statut,
          p.est_justifiee ? "Oui" : "Non"
        ]);
      });

      const retardCount = allPresences.filter(p => p.statut === 'RETARD').length;
      const absentCount = allPresences.filter(p => p.statut === 'ABSENT').length;

      doc.setFontSize(12);
      doc.text(`Filtres: Date: ${selectedDate || 'Toutes'} | Statut: ${selectedStatut || 'Retard & Absence'}`, 14, 30);
      doc.text(`Totaux: Retards: ${retardCount} | Absences: ${absentCount} | Total: ${allPresences.length}`, 14, 40);

      autoTable(doc, { 
        head: [tableColumn], 
        body: tableRows, 
        startY: 50, 
        styles: { fontSize: 10 }, 
        headStyles: { fillColor: [27, 1, 155] } 
      });
      
      const fileName = `justifications_${selectedDate || 'all'}_${new Date().toLocaleDateString().replace(/\//g, '-')}_${currentOrganisation?.nom || ''}.pdf`;
      doc.save(fileName);
    } catch (err) {
      console.error("Erreur lors de la génération du PDF", err);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Impossible de générer le PDF'
      });
    } finally {
      setLoading(false);
    }
  };

  const periodStyles = {
    exceptionnellePeriod: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      borderRadius: '12px',
      backgroundColor: '#fff3cd',
      color: '#856404',
      border: '1px solid #ffeaa7',
      fontSize: '0.85rem',
      fontWeight: '500'
    },
    matinPeriod: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      borderRadius: '12px',
      backgroundColor: '#d1ecf1',
      color: '#0c5460',
      border: '1px solid #bee5eb',
      fontSize: '0.85rem',
      fontWeight: '500'
    },
    apresMidiPeriod: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      borderRadius: '12px',
      backgroundColor: '#f8d7da',
      color: '#721c24',
      border: '1px solid #f5c6cb',
      fontSize: '0.85rem',
      fontWeight: '500'
    },
    descentePeriod: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      borderRadius: '12px',
      backgroundColor: '#d4edda',
      color: '#155724',
      border: '1px solid #c3e6cb',
      fontSize: '0.85rem',
      fontWeight: '500'
    },
    pausePeriod: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      borderRadius: '12px',
      backgroundColor: '#e2e3e5',
      color: '#383d41',
      border: '1px solid #d6d8db',
      fontSize: '0.85rem',
      fontWeight: '500'
    },
    defaultPeriod: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      borderRadius: '12px',
      backgroundColor: '#e9ecef',
      color: '#495057',
      border: '1px solid #dee2e6',
      fontSize: '0.85rem',
      fontWeight: '500'
    }
  };

  return (
    <div style={{...styles.container, marginLeft: sidebarVisible ? 250 : 0}}>
      {/* Modal Détail */}
      {showDetailModal && detailJustif && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" style={{ color: '#1B019B' }}>
                  Détail de la Justification de {detailJustif.employe_nom} {detailJustif.employe_prenom}
                </h5>
                <button className="btn-close" onClick={() => setShowDetailModal(false)}></button>
              </div>
              <div className="modal-body">
                <p><strong>Texte :</strong></p>
                <p>{detailJustif.texte || 'Aucun texte fourni'}</p>

                {detailJustif.fichier && (
                  <>
                    <p><strong>Fichier joint :</strong></p>
                    <a href={detailJustif.fichier} target="_blank" rel="noopener noreferrer" 
                          style={styles.fileLink}>
                  <FaEye />  Voir le fichier joint
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="text-center mb-3">
        <h3 style={styles.header}>Présences à Justifier</h3>
        <hr style={styles.hr} />
      </div>

      {/* Header avec sélecteur d'organisation réutilisable */}
      <div style={styles.organisationHeader}>
        <OrganisationSelector 
          onOrganisationChange={handleOrganisationChange}
          selectedOrganisation={selectedOrganisation}
        />

        {currentOrganisation && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#666', fontSize: '14px' }}>
              Présences à justifier pour cette organisation
            </span>
          </div>
        )}
      </div>

      {currentOrganisation ? (
        <>
          <h1 style={styles.title}>
            📝 JUSTIFICATIONS - {currentOrganisation.nom}
          </h1>

          {/* Filtres */}
          <div style={styles.filterContainer}>
            <div style={styles.filterGroup}>
              <FaSearch style={styles.filterIcon} />
              <input
                type="text"
                placeholder="Rechercher nom, prénom..."
                style={styles.searchBar}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            
            <div style={styles.filterGroup}>
              <FaCalendarAlt style={styles.filterIcon} />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={styles.dateInput}
              />
            </div>
            
            <div style={styles.filterGroup}>
              <FiAlertTriangle style={styles.filterIcon} />
              <select
                style={styles.select}
                value={selectedStatut}
                onChange={(e) => setSelectedStatut(e.target.value)}
              >
                <option value="">Retard & Absence</option>
                <option value="RETARD">Retard</option>
                <option value="ABSENT">Absent</option>
              </select>
            </div>
            
            <div style={styles.filterGroup}>
              <label style={styles.pageSizeLabel}>Afficher :</label>
              <select
                style={styles.select}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(e.target.value === 'all' ? 'all' : parseInt(e.target.value));
                }}
                disabled={loading}
              >
                {generatePageSizeOptions().map((size, idx) => (
                  <option key={idx} value={size}>
                    {size === 'all' ? 'Tous' : size}
                  </option>
                ))}
              </select>
            </div>
            {(user?.is_super_admin || user?.permissions?.includes("EXPORT_PDF")) && (
            <div>
              <button
                className="btn btn-danger"
                onClick={handleExportPDF}
                disabled={loading}
                style={styles.exportButton}
              >
                <FaDownload style={{ marginRight: '8px' }} />
                {loading ? 'Génération...' : 'Exporter PDF'}
              </button>
            </div>
            )}
          </div>

          {/* Tableau */}
          <div className="card shadow-sm mb-4" style={{ borderRadius: '15px', border: 'none' }}>
            <div className="card-body table-responsive p-0">
              <table className="table table-hover text-center align-middle m-0">
                <thead>
                  <tr style={styles.tableHeader}>
                    <th>Nom</th>
                    <th>Prénom</th>
                    <th>Date</th>
                    <th>Heure</th>
                    <th>Période</th>
                    <th>Statut</th>
                    <th>Justificatif</th>
                    <th>Justifié</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {presences.length ? presences.map((p) => {
                    const firstJustif = p.justifications && p.justifications.length > 0 ? p.justifications[0] : null;
                    return (
                      <tr key={p.id}>
                        <td>{p.employe_nom}</td>
                        <td>{p.employe_prenom}</td>
                        <td>{p.date}</td>
                        <td>{formatHeure(p.heure)}</td>

                        <td>
                          {p.periode === "autre" ? (
                            <span title={p.motif_sortie || 'Sortie hors période normale'} style={periodStyles.exceptionnellePeriod}>
                              <FiAlertTriangle /> Exceptionnelle
                            </span>
                          ) : p.periode === "matin" ? (
                            <span style={periodStyles.matinPeriod}>
                              <FiSunrise /> Matin
                            </span>
                          ) : p.periode === "apres_midi" ? (
                            <span style={periodStyles.apresMidiPeriod}>
                              <FiSunset /> Après-midi
                            </span>
                          ) : p.periode === "descente" ? (
                            <span style={periodStyles.descentePeriod}>
                              <FiArrowDownCircle /> Descente
                            </span>
                          ) : p.periode === "pause" ? (
                            <span style={periodStyles.pausePeriod}>
                              <FiCoffee /> Pause
                            </span>
                          ) : (
                            <span style={periodStyles.defaultPeriod}>
                              {p.periode?.charAt(0).toUpperCase() + p.periode?.slice(1).replace('_', ' ') || '-'}
                            </span>
                          )}
                        </td>

                        <td>
                          <span className={`badge ${p.statut === "RETARD" ? 'bg-warning' : 'bg-danger'} rounded-pill`}>
                            {p.statut}
                          </span>
                        </td>
                        <td>
                          {firstJustif ? (
                            <button
                              onClick={() => {
                                setDetailJustif({ 
                                  ...firstJustif, 
                                  employe_nom: p.employe_nom, 
                                  employe_prenom: p.employe_prenom,
                                  statut: p.statut 
                                });
                                setShowDetailModal(true);
                              }}
                              style={styles.detailButton}
                            >
                              <FaInfoCircle /> Détail
                            </button>
                          ) : (
                            '-'
                          )}
                        </td>

                        <td>
                          <span className={`badge rounded-pill ${p.est_justifiee ? 'bg-success' : 'bg-danger'}`}>
                            {p.est_justifiee ? "Oui" : "Non"}
                          </span>
                        </td>
                        <td>
                          {!p.est_justifiee && ( user?.is_super_admin || user?.permissions?.includes("JUSTIFY_CLOCKING")) && (
                            <button 
                              style={styles.justifierBtn} 
                              onClick={() => { setSelectedPresence(p); setShowModal(true); }}
                            >
                              Justifier
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan="9" className="text-muted py-4">
                        {loading ? 'Chargement en cours...' : 'Aucune présence à justifier.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

   
            {pagination.totalPages > 1 && (
              <div className="card-footer d-flex justify-content-center mt-3 bg-transparent border-top-0">
                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${!pagination.previous ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={handlePrevPage}
                        disabled={!pagination.previous || loading}
                        style={!pagination.previous ? styles.paginationDisabled : styles.paginationButton}
                      >
                        &laquo; Précédent
                      </button>
                    </li>
                    
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(pageNum)}
                            disabled={loading}
                            style={currentPage === pageNum ? { ...styles.paginationButton, ...styles.paginationActive } : styles.paginationButton}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    })}

                    <li className={`page-item ${!pagination.next ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={handleNextPage}
                        disabled={!pagination.next || loading}
                        style={!pagination.next ? styles.paginationDisabled : styles.paginationButton}
                      >
                        Suivant &raquo;
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>

          {/* Modal */}
          {showModal && (
            <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title" style={{ color: '#1B019B' }}>
                      Justifier le status {selectedPresence?.statut === 'RETARD' ? 'Retard' : 'Absent'} de {selectedPresence?.employe_nom} {selectedPresence?.employe_prenom}
                    </h5>
                    <button className="btn-close" onClick={() => setShowModal(false)}></button>
                  </div>
                  <div className="modal-body">
                    <textarea
                      className="form-control mb-3"
                      rows="3"
                      placeholder="Motif..."
                      value={justifTexte}
                      onChange={(e) => setJustifTexte(e.target.value)}
                    ></textarea>
                    <input type="file" className="form-control" onChange={(e) => setJustifFile(e.target.files[0])} />
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
                    <button style={styles.modalBtn} onClick={handleJustifier}>Valider</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-5">
          <p className="text-muted">Sélectionnez une organisation pour commencer</p>
        </div>
      )}
    </div>
  );
};

export default JustificationPage;